import { useState, useCallback } from 'react';
import { useApi } from '../hooks/useApi.js';
import { post } from '../lib/api.js';
import {
  BarChart2,
  TrendingUp,
  TrendingDown,
  Minus,
  Users,
  RefreshCw,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────

interface ClientRiskSnapshot {
  id: string;
  tenant_id: string;
  tenant_slug: string;
  client_name: string;
  human_risk_score: number;
  phishing_score: number;
  training_score: number;
  dark_web_score: number;
  posture_score: number;
  phishing_click_rate: number;
  training_completion_rate: number;
  unresolved_dark_web_findings: number;
  critical_posture_events_30d: number;
  total_users_monitored: number;
  score_7d_ago?: number;
  score_30d_ago?: number;
  trend_direction: 'improving' | 'declining' | 'stable';
  vs_smb_avg: number;
  vs_msp_avg: number;
  percentile?: number;
  computed_at: string;
  period_start: string;
  period_end: string;
}

interface SummaryData {
  total_clients: number;
  avg_risk_score: number;
  clients_below_smb_avg: number;
  clients_above_msp_avg: number;
  improving_clients: number;
  declining_clients: number;
  needs_attention: ClientRiskSnapshot[];
  benchmarks: { smb_avg: number; msp_avg: number; top_quartile: number };
}

interface ListResponse {
  data: ClientRiskSnapshot[];
}

interface SummaryResponse {
  data: SummaryData;
}

interface HistoryResponse {
  data: ClientRiskSnapshot[];
}

interface RefreshResult {
  data: {
    processed: number;
    succeeded: number;
    failed: number;
    results: Array<{ tenant_slug: string; success: boolean; error?: string }>;
  };
}

// ─── Helpers ───────────────────────────────────────────────────

type SortKey = 'human_risk_score' | 'client_name' | 'phishing_click_rate' | 'training_completion_rate' | 'unresolved_dark_web_findings' | 'critical_posture_events_30d' | 'vs_msp_avg';

function scoreColor(score: number): string {
  if (score < 40) return 'text-red-600';
  if (score <= 60) return 'text-yellow-600';
  return 'text-emerald-600';
}

function scoreBarColor(score: number): string {
  if (score < 40) return 'bg-red-500';
  if (score <= 60) return 'bg-yellow-400';
  return 'bg-emerald-500';
}

function scoreBgColor(score: number): string {
  if (score < 40) return 'bg-red-50 border-red-200';
  if (score <= 60) return 'bg-yellow-50 border-yellow-200';
  return 'bg-emerald-50 border-emerald-200';
}

function pctBadge(rate: number, invert = false): string {
  const pct = rate * 100;
  if (invert) {
    // For click rate: high is bad
    if (pct > 20) return 'bg-red-100 text-red-700';
    if (pct > 10) return 'bg-yellow-100 text-yellow-700';
    return 'bg-emerald-100 text-emerald-700';
  } else {
    // For completion rate: high is good
    if (pct >= 80) return 'bg-emerald-100 text-emerald-700';
    if (pct >= 50) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

// ─── Sparkline ─────────────────────────────────────────────────

function Sparkline({ history }: { history: ClientRiskSnapshot[] }) {
  if (history.length < 2) {
    return <p className="text-sm text-gray-400 py-2">Not enough history to display trend.</p>;
  }

  const scores = history.map((h) => h.human_risk_score);
  const max = Math.max(...scores, 100);
  const min = Math.min(...scores, 0);
  const range = max - min || 1;
  const width = 280;
  const height = 56;
  const pad = 4;

  const points = scores
    .map((s, i) => {
      const x = pad + (i / (scores.length - 1)) * (width - pad * 2);
      const y = height - pad - ((s - min) / range) * (height - pad * 2);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div className="flex items-center gap-4">
      <svg width={width} height={height} className="overflow-visible">
        <polyline
          points={points}
          fill="none"
          stroke="#10b981"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {scores.map((s, i) => {
          const x = pad + (i / (scores.length - 1)) * (width - pad * 2);
          const y = height - pad - ((s - min) / range) * (height - pad * 2);
          return <circle key={i} cx={x} cy={y} r={3} fill="#10b981" />;
        })}
      </svg>
      <div className="text-xs text-gray-500 space-y-0.5">
        {scores.map((s, i) => (
          <div key={i} className="flex items-center gap-1">
            <span className={`font-medium ${scoreColor(s)}`}>{s}</span>
            <span className="text-gray-400">{formatDate(history[i]?.computed_at ?? '')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Benchmark Bar ─────────────────────────────────────────────

function BenchmarkLegend() {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Industry Benchmarks</p>
      <div className="relative h-5 bg-gray-100 rounded-full overflow-hidden">
        {/* Color bands */}
        <div className="absolute inset-0 flex">
          <div className="bg-red-100" style={{ width: '40%' }} />
          <div className="bg-yellow-100" style={{ width: '22%' }} />
          <div className="bg-emerald-100" style={{ width: '38%' }} />
        </div>
        {/* SMB avg line */}
        <div className="absolute top-0 bottom-0 w-0.5 bg-red-400" style={{ left: '38%' }} />
        {/* MSP avg line */}
        <div className="absolute top-0 bottom-0 w-0.5 bg-yellow-500" style={{ left: '62%' }} />
        {/* Top quartile line */}
        <div className="absolute top-0 bottom-0 w-0.5 bg-emerald-600" style={{ left: '80%' }} />
      </div>
      <div className="flex justify-between mt-2 text-xs text-gray-500">
        <span>0</span>
        <span className="text-red-600 font-medium" style={{ marginLeft: '34%' }}>SMB avg (38)</span>
        <span className="text-yellow-700 font-medium">MSP avg (62)</span>
        <span className="text-emerald-700 font-medium">Top (80)</span>
        <span>100</span>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────

export function MspRiskPage() {
  const [sortKey, setSortKey] = useState<SortKey>('human_risk_score');
  const [sortAsc, setSortAsc] = useState(true);
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);
  const [historyCache, setHistoryCache] = useState<Record<string, ClientRiskSnapshot[]>>({});
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);

  const {
    data: listData,
    loading: listLoading,
    error: listError,
    refresh: reloadList,
  } = useApi<ListResponse>('/protect/msp-risk');

  const {
    data: summaryData,
    loading: summaryLoading,
    refresh: reloadSummary,
  } = useApi<SummaryResponse>('/protect/msp-risk?action=summary');

  const snapshots = listData?.data ?? [];
  const summary = summaryData?.data;

  // ── Sorting ──
  const sorted = [...snapshots].sort((a, b) => {
    let av: number | string = a[sortKey] as number | string;
    let bv: number | string = b[sortKey] as number | string;
    if (sortKey === 'client_name') {
      av = a.client_name.toLowerCase();
      bv = b.client_name.toLowerCase();
    }
    if (av < bv) return sortAsc ? -1 : 1;
    if (av > bv) return sortAsc ? 1 : -1;
    return 0;
  });

  // ── Column sort click ──
  function handleSort(key: SortKey) {
    if (sortKey === key) setSortAsc((v) => !v);
    else { setSortKey(key); setSortAsc(true); }
  }

  // ── Row expand / history load ──
  const handleRowExpand = useCallback(
    async (slug: string) => {
      if (expandedSlug === slug) {
        setExpandedSlug(null);
        return;
      }
      setExpandedSlug(slug);
      if (!historyCache[slug]) {
        try {
          const resp = await import('../lib/api.js').then(({ get }) =>
            get<HistoryResponse>(`/protect/msp-risk/${slug}`),
          );
          setHistoryCache((prev) => ({ ...prev, [slug]: resp.data }));
        } catch {
          setHistoryCache((prev) => ({ ...prev, [slug]: [] }));
        }
      }
    },
    [expandedSlug, historyCache],
  );

  // ── Refresh all ──
  async function handleRefreshAll() {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      const resp = await post<RefreshResult>('/protect/msp-risk?action=refresh', {});
      setRefreshMsg(`Refreshed ${resp.data.succeeded}/${resp.data.processed} clients.`);
      reloadList();
      reloadSummary();
    } catch (err) {
      setRefreshMsg(err instanceof Error ? err.message : 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  }

  // ── Sort header helper ──
  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-emerald-600 ml-1">{sortAsc ? '↑' : '↓'}</span>;
  }

  function thClass(col: SortKey) {
    return `px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-gray-900 whitespace-nowrap${sortKey === col ? ' text-gray-900' : ''}`;
  }

  if (listLoading && snapshots.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading MSP risk dashboard…</div>
      </div>
    );
  }

  if (listError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{listError}</p>
        <button onClick={reloadList} className="mt-2 text-sm text-red-600 underline">
          Retry
        </button>
      </div>
    );
  }

  const lastComputed = snapshots[0]?.computed_at;

  return (
    <div>
      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BarChart2 className="w-6 h-6 text-emerald-600" />
            <h2 className="text-2xl font-bold text-gray-900">MSP Human Risk Dashboard</h2>
          </div>
          <p className="text-sm text-gray-500">
            Cross-client aggregated human risk scores — sorted worst first.
            {lastComputed && (
              <span className="ml-2 text-gray-400">Last computed: {formatDate(lastComputed)}</span>
            )}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={handleRefreshAll}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh All'}
          </button>
          {refreshMsg && <p className="text-xs text-gray-500">{refreshMsg}</p>}
        </div>
      </div>

      {/* ── Summary Cards ── */}
      {!summaryLoading && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-gray-400" />
              <p className="text-sm text-gray-500">Total Clients</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{summary.total_clients}</p>
          </div>

          <div className={`rounded-lg border p-4 ${scoreBgColor(summary.avg_risk_score)}`}>
            <p className="text-sm text-gray-500 mb-1">Avg Risk Score</p>
            <p className={`text-2xl font-bold ${scoreColor(summary.avg_risk_score)}`}>
              {summary.avg_risk_score}
              <span className="text-sm font-normal text-gray-400 ml-1">/100</span>
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500 mb-1">Below SMB Average</p>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-gray-900">{summary.clients_below_smb_avg}</p>
              {summary.clients_below_smb_avg > 0 && (
                <span className="flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                  <AlertTriangle className="w-3 h-3" /> Needs attention
                </span>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500 mb-1">Improving This Month</p>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-gray-900">{summary.improving_clients}</p>
              {summary.improving_clients > 0 && (
                <span className="flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                  <TrendingUp className="w-3 h-3" /> Trending up
                </span>
              )}
              {summary.declining_clients > 0 && (
                <span className="flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                  <TrendingDown className="w-3 h-3" /> {summary.declining_clients} declining
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Benchmark Legend ── */}
      <BenchmarkLegend />

      {/* ── Clients Table ── */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th
                className={`text-left ${thClass('client_name')}`}
                onClick={() => handleSort('client_name')}
              >
                Client <SortIcon col="client_name" />
              </th>
              <th
                className={`text-left ${thClass('human_risk_score')}`}
                onClick={() => handleSort('human_risk_score')}
              >
                Risk Score <SortIcon col="human_risk_score" />
              </th>
              <th
                className={`text-center ${thClass('phishing_click_rate')}`}
                onClick={() => handleSort('phishing_click_rate')}
              >
                Phishing Click Rate <SortIcon col="phishing_click_rate" />
              </th>
              <th
                className={`text-center ${thClass('training_completion_rate')}`}
                onClick={() => handleSort('training_completion_rate')}
              >
                Training Completion <SortIcon col="training_completion_rate" />
              </th>
              <th
                className={`text-center ${thClass('unresolved_dark_web_findings')}`}
                onClick={() => handleSort('unresolved_dark_web_findings')}
              >
                Dark Web <SortIcon col="unresolved_dark_web_findings" />
              </th>
              <th
                className={`text-center ${thClass('critical_posture_events_30d')}`}
                onClick={() => handleSort('critical_posture_events_30d')}
              >
                Posture Events (30d) <SortIcon col="critical_posture_events_30d" />
              </th>
              <th className="text-center px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Trend</th>
              <th
                className={`text-center ${thClass('vs_msp_avg')}`}
                onClick={() => handleSort('vs_msp_avg')}
              >
                vs MSP Avg <SortIcon col="vs_msp_avg" />
              </th>
              <th className="w-8 px-2 py-3" />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                  No client risk data available. Trigger a refresh to compute scores.
                </td>
              </tr>
            ) : (
              sorted.map((snap) => {
                const isExpanded = expandedSlug === snap.tenant_slug;
                const history = historyCache[snap.tenant_slug] ?? [];

                return (
                  <>
                    <tr
                      key={snap.id}
                      className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors${isExpanded ? ' bg-emerald-50/40' : ''}`}
                      onClick={() => handleRowExpand(snap.tenant_slug)}
                    >
                      {/* Client Name */}
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {snap.client_name}
                        {snap.total_users_monitored > 0 && (
                          <span className="ml-2 text-xs text-gray-400 font-normal">
                            {snap.total_users_monitored} users
                          </span>
                        )}
                      </td>

                      {/* Risk Score with bar */}
                      <td className="px-4 py-3 min-w-[140px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${scoreBarColor(snap.human_risk_score)}`}
                              style={{ width: `${snap.human_risk_score}%` }}
                            />
                          </div>
                          <span className={`font-bold text-sm w-8 text-right tabular-nums ${scoreColor(snap.human_risk_score)}`}>
                            {snap.human_risk_score}
                          </span>
                        </div>
                      </td>

                      {/* Phishing Click Rate */}
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${pctBadge(snap.phishing_click_rate, true)}`}>
                          {(snap.phishing_click_rate * 100).toFixed(1)}%
                        </span>
                      </td>

                      {/* Training Completion */}
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${pctBadge(snap.training_completion_rate)}`}>
                          {(snap.training_completion_rate * 100).toFixed(0)}%
                        </span>
                      </td>

                      {/* Dark Web Findings */}
                      <td className="px-4 py-3 text-center">
                        <span className={`font-medium ${snap.unresolved_dark_web_findings > 5 ? 'text-red-600' : snap.unresolved_dark_web_findings > 0 ? 'text-yellow-600' : 'text-emerald-600'}`}>
                          {snap.unresolved_dark_web_findings}
                        </span>
                      </td>

                      {/* Critical Posture Events */}
                      <td className="px-4 py-3 text-center">
                        <span className={`font-medium ${snap.critical_posture_events_30d > 3 ? 'text-red-600' : snap.critical_posture_events_30d > 0 ? 'text-yellow-600' : 'text-emerald-600'}`}>
                          {snap.critical_posture_events_30d}
                        </span>
                      </td>

                      {/* Trend */}
                      <td className="px-4 py-3 text-center">
                        {snap.trend_direction === 'improving' && (
                          <span className="flex items-center justify-center gap-1 text-emerald-600">
                            <TrendingUp className="w-4 h-4" />
                            <span className="text-xs font-medium">Improving</span>
                          </span>
                        )}
                        {snap.trend_direction === 'declining' && (
                          <span className="flex items-center justify-center gap-1 text-red-500">
                            <TrendingDown className="w-4 h-4" />
                            <span className="text-xs font-medium">Declining</span>
                          </span>
                        )}
                        {snap.trend_direction === 'stable' && (
                          <span className="flex items-center justify-center gap-1 text-gray-400">
                            <Minus className="w-4 h-4" />
                            <span className="text-xs font-medium">Stable</span>
                          </span>
                        )}
                      </td>

                      {/* vs MSP Avg */}
                      <td className="px-4 py-3 text-center">
                        <span className={`font-medium tabular-nums ${snap.vs_msp_avg >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {snap.vs_msp_avg >= 0 ? '+' : ''}{snap.vs_msp_avg}
                        </span>
                      </td>

                      {/* Expand icon */}
                      <td className="px-2 py-3 text-center text-gray-400">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </td>
                    </tr>

                    {/* ── Expanded Detail Row ── */}
                    {isExpanded && (
                      <tr key={`${snap.id}-detail`} className="bg-emerald-50/30 border-b border-gray-100">
                        <td colSpan={9} className="px-6 py-4">
                          <div className="flex flex-wrap gap-8">
                            {/* Score breakdown */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Score Breakdown</p>
                              <div className="space-y-1.5 text-sm">
                                <div className="flex items-center gap-3">
                                  <span className="w-36 text-gray-600">Phishing Safety</span>
                                  <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${scoreBarColor(snap.phishing_score / 40 * 100)}`} style={{ width: `${(snap.phishing_score / 40) * 100}%` }} />
                                  </div>
                                  <span className="font-medium text-gray-700 tabular-nums">{snap.phishing_score.toFixed(1)}/40</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="w-36 text-gray-600">Training</span>
                                  <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${scoreBarColor(snap.training_score / 25 * 100)}`} style={{ width: `${(snap.training_score / 25) * 100}%` }} />
                                  </div>
                                  <span className="font-medium text-gray-700 tabular-nums">{snap.training_score.toFixed(1)}/25</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="w-36 text-gray-600">Dark Web</span>
                                  <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${scoreBarColor(snap.dark_web_score / 20 * 100)}`} style={{ width: `${(snap.dark_web_score / 20) * 100}%` }} />
                                  </div>
                                  <span className="font-medium text-gray-700 tabular-nums">{snap.dark_web_score}/20</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="w-36 text-gray-600">SaaS Posture</span>
                                  <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${scoreBarColor(snap.posture_score / 15 * 100)}`} style={{ width: `${(snap.posture_score / 15) * 100}%` }} />
                                  </div>
                                  <span className="font-medium text-gray-700 tabular-nums">{snap.posture_score}/15</span>
                                </div>
                              </div>
                            </div>

                            {/* Score history sparkline */}
                            <div className="flex-1 min-w-[300px]">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Score History</p>
                              {history.length === 0 ? (
                                <p className="text-sm text-gray-400">Loading history…</p>
                              ) : (
                                <Sparkline history={history} />
                              )}
                            </div>

                            {/* Metadata */}
                            <div className="text-xs text-gray-400 self-end">
                              <p>Period: {formatDate(snap.period_start)} – {formatDate(snap.period_end)}</p>
                              <p>Computed: {formatDate(snap.computed_at)}</p>
                              {snap.percentile !== undefined && (
                                <p>Percentile (vs portfolio): {snap.percentile}th</p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
